'use client';

import {
  type AbstractIntlMessages,
  NextIntlClientProvider as IntlClientProvider,
} from 'next-intl';
import { type PropsWithChildren, useCallback } from 'react';

import getLocaleFormatting from '@/utils/getLocaleFormatting';
import { logToDataDog } from '@/utils/logToDatadog';

interface NextIntlClientProviderProps extends PropsWithChildren {
  readonly locale: string;
  readonly messages: AbstractIntlMessages;
}
const NextIntlClientProvider = ({
  children,
  locale,
  messages,
}: NextIntlClientProviderProps) => {
  const handleError = useCallback(
    (err: any) => {
      // Ignore errors if the messages data is not ready yet.
      if (!messages || !Object.keys(messages).length) {
        return;
      }

      if (
        ['MISSING_TRANSLATION', 'MISSING_DATA', 'MISSING_MESSAGE'].includes(
          err.code,
        )
      ) {
        // output to console
        console.error('Translation error:', err);

        // report to datadog
        const regex = /"([^"\\]*(\\.[^"\\]*)*)"/g;
        // error messages include the message_key & locale, each wrapped in double-quotes
        const [message_key, locale] = err.message?.match(regex) ?? [];

        logToDataDog('app', 'ui_translation_error', {
          cause: JSON.stringify(err),
          message_key,
          locale,
        });
      } else {
        throw err;
      }
    },
    [messages],
  );

  return (
    <IntlClientProvider
      locale={locale}
      messages={messages}
      timeZone="UTC" // default
      defaultTranslationValues={getLocaleFormatting}
      onError={handleError}
    >
      {children}
    </IntlClientProvider>
  );
};

export default NextIntlClientProvider;
